import { DATE_FORMATS, DateTime } from "@bps/date-time";
import {
  DatePickerField,
  Stack,
  Text,
  TextBadge,
  TextBadgeColor,
  TextBadgeSize,
  TimePickerField,
  useFieldContext,
  useTheme
} from "@bps/fluent-ui";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import { Permissions } from "@libs/permissions/permissions.enum";

import { convertToDateTime } from "../utils";

interface RolloutTimePickerProps {
  isReadOnly: boolean;
  datePickerName: string;
  timePickerName: string;
  isStart: boolean;
}

export const RolloutTimePicker: React.FC<RolloutTimePickerProps> = ({
  isReadOnly,
  datePickerName,
  timePickerName,
  isStart
}) => {
  const theme = useTheme();

  const { data: hasDeployPermission } = usePermissionsCheckQuery(
    Permissions.PltFieldManagementDeploy
  );

  const { value: dateValue } = useFieldContext<Date>(datePickerName);
  const { value: timeValue } = useFieldContext<string>(timePickerName);

  return (
    <Stack>
      <Stack
        horizontal
        horizontalAlign="start"
        verticalAlign="center"
        tokens={{ childrenGap: theme.spacing.m }}
        styles={{ root: { minWidth: "346px" } }}
      >
        <TextBadge
          badgeColor={isStart ? TextBadgeColor.green : TextBadgeColor.red}
          badgeSize={TextBadgeSize.small}
        >
          {isStart ? "Start" : "End"}
        </TextBadge>

        <DatePickerField
          disabled={isReadOnly || !hasDeployPermission}
          name={datePickerName}
          required
        />
        <TimePickerField
          disabled={isReadOnly || !hasDeployPermission}
          name={timePickerName}
        />
      </Stack>
      <CurrentTimezoneInfo dateValue={dateValue} timeValue={timeValue} />
    </Stack>
  );
};

const CurrentTimezoneInfo: React.FC<CurrentTimezoneInfoProps> = ({
  dateValue,
  timeValue
}) => {
  if (!dateValue || !timeValue) {
    return null;
  }

  return (
    <Text variant="xSmall">
      {convertToDateTime(dateValue ?? new Date(0, 0, 0), timeValue ?? "00:00")
        .setZone(DateTime.now().timeZone)
        .toFormat(DATE_FORMATS.LOCALIZED_DATE_TIME)}
    </Text>
  );
};

interface CurrentTimezoneInfoProps {
  dateValue?: Date;
  timeValue?: string;
}
